import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from 'AppRoutes/BeerStoreShipping/styles';
import GrowlerPricingBreakdown from 'components/GrowlerPricingBreakdown';
import { useGrowlersCartPricing } from 'hooks/useGrowlersCartPricing';
import { useGrowlersCashback } from 'hooks/useGrowlersCashback';
import { useGrowlerCart } from 'services/cart/GrowlerCartContext';
import { formatCurrencyBRL, roundCurrency } from 'utils/currency';
import {
  formatGrowlerAddressSummary,
  GROWLER_DELIVERY_TIME,
  GROWLER_DELIVERY_TIME_LINES,
} from 'utils/growlersFlow';
import {
  GROWLER_CUSTOMER_STORAGE_KEY,
  GROWLER_ORDER_STORAGE_KEY,
  type GrowlerCustomer,
} from 'utils/types/GrowlerCustomer';

const GrowlerShipping: React.FC = () => {
  const navigate = useNavigate();
  const { cart } = useGrowlerCart();
  const [customer, setCustomer] = useState<GrowlerCustomer | null>(null);
  const pricing = useGrowlersCartPricing(cart.totalItems, cart.totalValue);
  const merchandiseSubtotal = roundCurrency(
    Math.max(0, pricing.subtotal - pricing.discount),
  );
  const cashback = useGrowlersCashback(merchandiseSubtotal);

  useEffect(() => {
    if (cart.items.length === 0) {
      navigate('/growlers');
      return;
    }

    const raw = localStorage.getItem(GROWLER_CUSTOMER_STORAGE_KEY);
    if (!raw) {
      navigate('/growlers/order-confirmation');
      return;
    }

    try {
      setCustomer(JSON.parse(raw));
    } catch {
      navigate('/growlers/order-confirmation');
    }
  }, [cart.items.length, navigate]);

  const handleProceed = () => {
    const raw = localStorage.getItem(GROWLER_ORDER_STORAGE_KEY);
    const current = raw ? JSON.parse(raw) : {};
    localStorage.setItem(
      GROWLER_ORDER_STORAGE_KEY,
      JSON.stringify({
        ...current,
        customer,
        shipping: {
          id: 'entrega',
          price: pricing.shippingCost,
          description:
            pricing.shippingCost === 0 ? 'Entrega grátis' : 'Taxa de entrega',
          deliveryTime: GROWLER_DELIVERY_TIME,
        },
      }),
    );
    navigate('/growlers/payment');
  };

  if (!customer) return null;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography variant="h4" sx={styles.title}>
          Finalizar Pedido
        </Typography>
        <Typography variant="subtitle1" sx={styles.subtitle}>
          Confirme o endereço e a entrega
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Typography variant="h6" sx={styles.sectionTitle}>
            ENDEREÇO DE ENTREGA
          </Typography>
          <Card sx={styles.card}>
            <CardContent>
              <Typography sx={styles.addressText}>
                {customer.fullName}
              </Typography>
              {formatGrowlerAddressSummary(customer).map((line, index) => (
                <Typography key={`${line}-${index}`} sx={styles.addressText}>
                  {line}
                </Typography>
              ))}
              <Typography sx={styles.addressDetails}>
                WhatsApp: {customer.whatsapp}
              </Typography>
            </CardContent>
          </Card>

          <Typography variant="h6" sx={styles.sectionTitle}>
            ENTREGA
          </Typography>
          <RadioGroup value="entrega">
            <Card sx={styles.shippingCard}>
              <CardContent sx={styles.shippingCardContent}>
                <FormControlLabel
                  value="entrega"
                  control={<Radio sx={styles.radio} />}
                  label={
                    <Box>
                      <Typography sx={styles.shippingTitle}>Entrega</Typography>
                      {GROWLER_DELIVERY_TIME_LINES.map(line => (
                        <Typography key={line} sx={styles.shippingDetails}>
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  }
                />
                <Typography variant="h6" sx={styles.shippingPrice}>
                  {pricing.shippingCost === 0
                    ? 'GRÁTIS'
                    : `R$ ${formatCurrencyBRL(pricing.shippingCost)}`}
                </Typography>
              </CardContent>
            </Card>
          </RadioGroup>
        </Grid>

        <Grid item xs={12} md={5}>
          <Typography variant="h6" sx={styles.sectionTitle}>
            RESUMO DO PEDIDO
          </Typography>
          <Card sx={styles.summaryCard}>
            <CardContent>
              {cart.items.map(item => (
                <Box key={item.id} sx={styles.cartItem}>
                  <Box sx={styles.itemInfo}>
                    <Typography sx={styles.itemName}>
                      {item.growler.name}
                    </Typography>
                    <Typography sx={styles.itemDetails}>
                      {item.growler.brewery} • {item.quantity}x
                    </Typography>
                  </Box>
                  <Typography sx={styles.itemPrice}>
                    R$ {(item.growler.price * item.quantity).toFixed(2)}
                  </Typography>
                </Box>
              ))}
              <Divider sx={styles.summaryDivider} />
              <GrowlerPricingBreakdown
                pricing={pricing}
                cashbackEarnAmount={cashback.earnAmount}
                cashbackEarnPercent={cashback.earnPercent}
                rowSx={styles.summaryRow}
                totalRowSx={styles.totalRow}
                totalLabelSx={styles.totalLabel}
                totalValueSx={styles.totalValue}
                hintSx={{ display: 'block', mb: 1, color: 'text.secondary' }}
              />
              <Button
                variant="contained"
                fullWidth
                onClick={handleProceed}
                sx={styles.checkoutButton}
                disabled={pricing.loading}
              >
                Ir para pagamento
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GrowlerShipping;
